import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { catchError, from, mergeMap, Observable, of, throwError } from 'rxjs';

import {
  API_BASE_URL,
  ApiException,
  ApiResponseOfSearchClaimResponse,
  SearchClaimRequest
} from '@benefitsui/app/api/benefits-clients';
import { ClientBase, HttpRequestOptions } from '@benefitsui/app/api/client-base';
import { Constants } from '@benefitsui/app/shared/constants/constants';

@Injectable({
  providedIn: 'root'
})
export class ClaimsServiceClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
    super();
    this.http = http;
    this.baseUrl = baseUrl ?? '';
  }

  /**
   * Search for claim data based on policy number(s).
   * @param body (optional) The search claim request.
   * @return OK
   */
  searchClaim(body: SearchClaimRequest): Observable<ApiResponseOfSearchClaimResponse | undefined> {
    let searchClaimApiUrl = this.baseUrl + '/v1/claims/search-claim';
    searchClaimApiUrl = searchClaimApiUrl.replace(/[?&]$/, '');

    const jsonBody = JSON.stringify(body);

    const httpRequestOptions: HttpRequestOptions = {
      body: jsonBody,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json-patch+json',
        Accept: 'application/json',
        [Constants.X_BDPORTAL_SECRET_HEADER]: Constants.X_BDPORTAL_SECRET_VALUE
      })
    };

    const result = from(this.transformOptions(httpRequestOptions))
      .pipe(
        mergeMap((httpRequestOptionsTransformed) => {
          const result = this.http.request('post', searchClaimApiUrl, httpRequestOptionsTransformed);
          return result;
        })
      )
      .pipe(
        mergeMap((response) => {
          const result = this.transformResult(searchClaimApiUrl, response, (r) => this.processSearchClaim(r));
          return result;
        })
      )
      .pipe(
        catchError((response) => {
          if (response instanceof HttpResponseBase) {
            try {
              const result = this.transformResult(searchClaimApiUrl, response, (r) => this.processSearchClaim(r));
              return result;
            } catch (e) {
              return throwError(() => new Error(String(e))) as Observable<ApiResponseOfSearchClaimResponse>;
            }
          } else return throwError(() => new Error(response)) as Observable<ApiResponseOfSearchClaimResponse>;
        })
      );

    return result;
  }

  protected processSearchClaim(response: HttpResponseBase): Observable<ApiResponseOfSearchClaimResponse | undefined> {
    const status = response.status;

    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (response as any)?.error instanceof Blob
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (response as any).error
          : undefined;

    const responseHeaders: HttpHeaders = new HttpHeaders();

    if (response.headers) {
      for (const key of response.headers.keys()) {
        const value = response.headers.get(key) || '';
        responseHeaders.append(key, value);
      }
    }

    if (status === 200) {
      const result = this.blobToText(responseBlob).pipe(
        mergeMap((responseText: string) => {
          const responseParsed: ApiResponseOfSearchClaimResponse = JSON.parse(responseText);

          const result200 = responseText === '' ? undefined : responseParsed;

          return of(result200);
        })
      );

      return result;
    } else if (status !== 200 && status !== 204) {
      return this.blobToText(responseBlob).pipe(
        mergeMap((responseText: string) => {
          return this.throwException('An unexpected server error occurred.', status, responseText, responseHeaders);
        })
      );
    }
    return of(undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private blobToText(blob: any): Observable<string> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Observable<string>((observer: any) => {
      if (!blob) {
        observer.next('');
        observer.complete();
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          const target = event.target as FileReader | null;
          observer.next(target?.result ?? '');
          observer.complete();
        };
        reader.readAsText(blob);
      }
    });
  }

  private throwException(message: string, status: number, response: string, headers: HttpHeaders) {
    return throwError(() => new ApiException(message, status, response, headers, null));
  }
}
